import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import "./AuthenticationContext.css";
import ServiceTransaction from "../services/ServiceTransaction";
import ServiceClient from "../services/ServiceClient";
import { useServiceClient } from "../services/ServiceClientContext";
import { UserProfileDetails } from "../services/brainflex/UsersServiceClient";

interface AuthContextType {
  isLoggedIn: boolean;
  user: { name?: string; email?: string } | null;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthenticationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const serviceClient: ServiceClient = useServiceClient();

  // State
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<{ name?: string; email?: string } | null>(null);
  const [showLoginMessage, setShowLoginMessage] = useState(false);


  const login = () => {
    setShowLoginMessage(true);
    setTimeout(() => {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      window.location.href = `${apiBaseUrl}/auth`;
    }, 500);
  };

  const logout = async () => {
    try {
      const transaction = await serviceClient.post('/auth/logout');
      if (transaction.response.isSuccess) {
        setIsLoggedIn(false);
        setUser(null);
      }
    } catch (error) {
      console.error("Error logging out: ", error);
    }

    setIsLoggedIn(false);
    setUser(null);
  };

  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        // serviceClient is captured from the component scope above - no hook violation
        const transaction: ServiceTransaction<UserProfileDetails> = await serviceClient.get<UserProfileDetails>("/users/me");
        if (transaction.response.isSuccess) {
          setIsLoggedIn(true);
          const data = transaction.response.success!.data;
          setUser({ name: data.givenName + " " + data.familyName, email: data.emailAddress });
        } else {
          setIsLoggedIn(false);
          setUser(null);
        }
      } catch (error) {
        console.error("Error fetching loginStatus:", error);
      }
    };
    checkLoginStatus();
  }, [serviceClient]);

  return (
    <AuthContext.Provider value={{ isLoggedIn, user, login, logout }}>
      {showLoginMessage && (
        <div className="auth-login-message-banner">
          Log in required. Redirecting...
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthentication = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuthentication must be used within AuthenticationProvider");
  return ctx;
};
